import { useMemo } from 'react'
import { useStyles } from './styles'
import type { ContentLayoutProps } from './types'
import useBreakpoints from '@/utils/useBreakpoints'
import { LayoutHeader } from '../Header'
import { LayoutFooter } from '../Footer'
import { LayoutActionBar } from '../ActionBar'
import { RenderIf } from '@/components/RenderIf'
import { Container } from '@/components/Container'
import { FunctionComponentChildren } from '@/@types/react'
import { AnimateEnter } from '@/components/Animate'

export const ContentLayout: FunctionComponentChildren<ContentLayoutProps> = ({
  children,
  hasHeader,
  title,
  caption,
  headerSize,
  hideFooter,
  layoutFooterProps,
  primaryActionTooltip,
  secondaryActionTooltip,
  negativeActionTooltip,
  primaryActionLabel,
  secondaryActionLabel,
  negativeActionLabel,
  primaryActionProps,
  secondaryActionProps,
  negativeActionProps,
  primaryAction,
  secondaryAction,
  negativeAction,
  hasPrimaryAction,
  hasSecondaryAction,
  hasNegativeAction,
  customActionBarContent,
  customFooter,
  fullHeight = true,
  styles,
  hasLayoutMenu,
  layoutMenu,
  showLayoutMenuOutsideContainer,
}) => {
  const { isPhone } = useBreakpoints()

  const canShowActionBar = useMemo(
    () =>
      hasPrimaryAction ||
      hasSecondaryAction ||
      hasNegativeAction ||
      Boolean(customActionBarContent),
    [
      hasPrimaryAction,
      hasSecondaryAction,
      hasNegativeAction,
      customActionBarContent,
    ],
  )

  const quantityOfButtonsInActionBar = () => {
    const actions = [hasPrimaryAction, hasSecondaryAction, hasNegativeAction]
    const trueActions = actions.filter(item => !!item).length
    return trueActions
  }

  const defaultStyles = useStyles({
    canShowActionBar,
    isPhone,
    fullHeight,
    quantityOfButtonsInActionBar: quantityOfButtonsInActionBar(),
  })

  return (
    <>
      <RenderIf condition={showLayoutMenuOutsideContainer}>
        {layoutMenu}
      </RenderIf>
      <Container css={defaultStyles?.root} style={styles?.root}>
        <div css={defaultStyles.content} style={styles?.body}>
          <AnimateEnter>
            <RenderIf
              condition={hasLayoutMenu && !showLayoutMenuOutsideContainer}
            >
              <div css={defaultStyles.layoutMenuContainer}>
                {layoutMenu}
                <RenderIf condition={!isPhone}>
                  <hr />
                </RenderIf>
              </div>
            </RenderIf>

            {!hasHeader || (
              <LayoutHeader
                title={title}
                caption={caption}
                size={headerSize || 'headingMedium'}
              />
            )}
            {children}
          </AnimateEnter>
        </div>

        <RenderIf condition={canShowActionBar}>
          <LayoutActionBar
            styles={styles?.actionBar}
            primaryActionTooltip={primaryActionTooltip}
            secondaryActionTooltip={secondaryActionTooltip}
            negativeActionTooltip={negativeActionTooltip}
            primaryActionLabel={primaryActionLabel}
            secondaryActionLabel={secondaryActionLabel}
            negativeActionLabel={negativeActionLabel}
            primaryActionProps={primaryActionProps}
            secondaryActionProps={secondaryActionProps}
            negativeActionProps={negativeActionProps}
            primaryAction={primaryAction}
            secondaryAction={secondaryAction}
            negativeAction={negativeAction}
            hasPrimaryAction={hasPrimaryAction}
            hasSecondaryAction={hasSecondaryAction}
            hasNegativeAction={hasNegativeAction}
            customContent={customActionBarContent}
          />
        </RenderIf>

        <RenderIf condition={!hideFooter}>
          <footer css={defaultStyles.footer} style={styles?.footer}>
            <LayoutFooter {...layoutFooterProps}>{customFooter}</LayoutFooter>
          </footer>
        </RenderIf>
      </Container>
    </>
  )
}
